// frontend/src/pages/PaymentSuccess.jsx
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from '../config/axios';

const PaymentSuccess = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [transactionData, setTransactionData] = useState(null);
    const location = useLocation();
    const navigate = useNavigate();
    const { updateUser } = useAuth();

    const params = new URLSearchParams(location.search);
    const preferenceId = params.get('preference_id') || params.get('external_reference');
    const amount = params.get('amount');
    const paymentId = params.get('payment_id');
    const status = params.get('status');

    useEffect(() => {
        const verifyPayment = async () => {
            try {
                if (!preferenceId) throw new Error('No se recibió ID de preferencia');

                const response = await axios.get(`/payments/verify/${preferenceId}`);

                if (response.data.success) {
                    setTransactionData(response.data.transaction);
                    if (response.data.user) updateUser(response.data.user);
                } else {
                    setError(response.data.message || 'Error al verificar el pago');
                }
            } catch (err) {
                console.error('Error verificando pago:', err);
                setError('No pudimos verificar el estado de tu pago. Por favor contacta a soporte.');
            } finally {
                setLoading(false);
            }
        };

        verifyPayment();
    }, [preferenceId, updateUser]);

    const ticketAmount = amount || Math.abs(transactionData?.amount) || '';

    return (
        <div
            className="min-h-screen flex items-center justify-center px-4 py-12"
            style={{ backgroundColor: '#0A1929' }}
        >
            <div className="w-full max-w-md">

                {loading ? (
                    <div
                        className="rounded-[24px] p-10 text-center shadow-[0_20px_60px_rgba(0,0,0,0.5)]"
                        style={{ backgroundColor: '#0E2238', border: '2px solid rgba(255,255,255,0.1)' }}
                    >
                        <div className="w-14 h-14 rounded-full border-4 border-t-[#2284DF] border-white/10
                                        animate-spin mx-auto mb-6" />
                        <h2 className="text-xl font-extrabold text-white mb-2">Verificando pago</h2>
                        <p className="text-white/50 text-sm">Confirmando el estado, solo unos segundos…</p>
                    </div>
                ) : (
                    <div
                        className="rounded-[24px] overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.5)]"
                        style={{ backgroundColor: '#0E2238', border: '2px solid rgba(255,255,255,0.1)' }}
                    >
                        {/* Icon + título */}
                        <div className="px-8 pt-10 pb-7 text-center"
                            style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                            {error ? (
                                <>
                                    <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5"
                                        style={{ backgroundColor: 'rgba(239,68,68,0.15)', border: '2px solid #EF4444' }}>
                                        <span className="text-4xl">❌</span>
                                    </div>
                                    <h2 className="text-2xl font-extrabold text-white mb-2">Error en la verificación</h2>
                                    <p className="text-white/50 text-sm">{error}</p>
                                </>
                            ) : (
                                <>
                                    <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5"
                                        style={{ backgroundColor: 'rgba(16,185,129,0.15)', border: '2px solid #10B981' }}>
                                        <span className="text-4xl">✅</span>
                                    </div>
                                    <h2 className="text-2xl font-extrabold text-white mb-2">¡Pago Exitoso!</h2>
                                    {ticketAmount && (
                                        <p className="text-white/60 text-sm">
                                            Se acreditaron{' '}
                                            <span className="text-[#FCE503] font-bold">{ticketAmount} tickets</span>{' '}
                                            a tu cuenta.
                                        </p>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Detalle */}
                        {!error && (
                            <div className="px-8 py-5"
                                style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                                <p className="text-xs font-bold uppercase tracking-widest text-white/30 mb-4">Detalle</p>
                                <div className="flex flex-col gap-3">
                                    <Row label="ID de pago" value={paymentId || 'N/A'} />
                                    <Row label="Estado" value={status || 'Aprobado'} valueClass="text-[#10B981] font-bold" />
                                    <Row label="Fecha" value={new Date().toLocaleString('es-AR', {
                                        day: '2-digit', month: '2-digit', year: 'numeric',
                                        hour: '2-digit', minute: '2-digit'
                                    })} />
                                </div>
                            </div>
                        )}

                        {/* Botones */}
                        <div className="px-8 py-6 flex flex-col gap-3">
                            <button
                                onClick={() => navigate('/')}
                                className="w-full h-12 rounded-[14px] font-bold text-sm uppercase tracking-wider
                                           transition-transform hover:scale-[1.02] active:scale-[0.98]"
                                style={{ backgroundColor: '#FCE503', color: '#122436', border: '2px solid #E5CF03' }}
                            >
                                Ir al inicio
                            </button>
                            <button
                                onClick={() => navigate('/recharge')}
                                className="w-full h-12 rounded-[14px] font-bold text-sm uppercase tracking-wider
                                           text-white/50 hover:text-white/80 transition-colors"
                            >
                                Comprar más tickets
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const Row = ({ label, value, valueClass = 'text-white font-semibold' }) => (
    <div className="flex justify-between items-center text-sm">
        <span className="text-white/40">{label}</span>
        <span className={valueClass}>{value}</span>
    </div>
);

export default PaymentSuccess;
